<template>
    <section class="mx-auto max-w-6xl xl:px-10" role="region" aria-label="Information section">

        <div class="px-4">
            <div class="grid grid-cols-12 gap-y-6 pb-6 xl:gap-x-12 md:gap-x-8 lg:pb-12">
                <div class="grid col-span-12 items-center md:col-span-6">
                 <div>
                  <h2 class="text-2xl text-center xl:!leading-tight md:text-left font-ww-bold xl:text-42 md:text-3xl">
                        {{ props.title }}
                    </h2>

                    <div class="mt-6 text-base xl:text-xl md:text-lg font-ww-regular">
                        <span v-html="props.introText"></span>

                        <ul v-if="props.listItems && props.listItems.length > 0" class="mt-4 list-disc list-inside custom-marker-r">
                            <li v-for="(item, index) in props.listItems" :key="index">
                                {{ item.text || item }}
                            </li>
                        </ul>

                        <div class="mt-4">
                            <span v-html="props.conclusionText"></span>
                        </div>

                    </div>
                 </div>

                </div>

                <div class="grid col-span-12 items-center md:col-span-6">
                    <nuxt-img 
                        :src="props.imageSrc" 
                        class="mx-auto w-full lg:w-[430px]" 
                        :alt="props.imageAlt"
                        format="webp"
                        loading="lazy"
                        width="429"
                        height="575"
                        sizes="xs:300px sm:440px md:760px lg:421px xl:493px 2xl:429px"
                    />
                </div>

            </div>
        </div>
    </section>
</template>

<script setup>
// Props
const props = defineProps({
  title: {
    type: String,
    default: 'The Hidden Downside of Your Morning Coffee'
  },
  introText: {
    type: String,
    default: 'Sure, caffeine gives you a boost. But here\'s the dark side nobody talks about:'
  },
  listItems: {
    type: Array,
    default: () => [
      { text: 'That jittery, anxious energy that makes it hard to sit still (let alone focus).' },
      { text: 'The 2pm crash that leaves you groggy and reaching for another cup—or a candy bar.' },
      { text: 'The restless nights that come from "just one more" in the afternoon.' }
    ]
  },
  conclusionText: {
    type: String,
    default: 'The problem isn\'t caffeine itself.<br>It\'s the way most of us use it.<br><br>That\'s where <strong>caffeine engineering</strong> comes in—a smarter way to unlock the benefits of caffeine, while avoiding the drawbacks.'
  },
  imageSrc: {
    type: String,
    default: '/assets/images/people-right.jpg'
  },
  imageAlt: {
    type: String,
    default: 'Caffeine Engineering'
  },
})

defineBuilderComponent({
  friendlyName: 'Left Info Right Image Section',
  inputs: [
    {
      name: 'title',
      type: 'text',
      required: true,
      defaultValue: 'The Hidden Downside of Your Morning Coffee'
    },
    {
      name: 'introText',
      type: 'richText',
      defaultValue: 'Sure, caffeine gives you a boost. But here\'s the dark side nobody talks about:'
    },
    {
      name: 'listItems',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'text',
          defaultValue: ''
        }
      ],
      defaultValue: [
        { text: 'That jittery, anxious energy that makes it hard to sit still (let alone focus).' },
        { text: 'The 2pm crash that leaves you groggy and reaching for another cup—or a candy bar.' },
        { text: 'The restless nights that come from "just one more" in the afternoon.' }
      ],
      helperText: 'List of bullet points. Leave empty to hide the list.'
    },
    {
      name: 'conclusionText',
      type: 'richText',
      required: true,
      defaultValue: 'The problem isn\'t caffeine itself.<br>It\'s the way most of us use it.<br><br>That\'s where <strong>caffeine engineering</strong> comes in—a smarter way to unlock the benefits of caffeine, while avoiding the drawbacks.'
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      required: true,
      defaultValue: '/assets/images/people-right.jpg'
    },
    {
      name: 'imageAlt',
      type: 'text',
      required: true,
      defaultValue: 'Caffeine Engineering'
    },
  ]
})
</script>